// React and hooks
import React, { useState, useRef, useLayoutEffect } from 'react'

// Third-party libraries
import moment from 'moment'
import { Col, Dropdown, Row } from 'react-bootstrap'

// Types and interfaces
import { IAppointment } from '@dataTypes/appointment.type'
import { ProviderColor } from '@dataTypes/provider.types'

// Constants and utilities
import {
  getProviderBackgroundColor,
  getProviderBorderColor,
  getProviderTextColor,
} from '@constants/Providers'

type GroupedAppointmentsByDate = {
  [date: string]: {
    totalAppointments: number
    providers: {
      provider_id: string
      first_name: string
      last_name: string
      provider_color: ProviderColor
      appointments: IAppointment[]
    }[]
  }
}

export const CustomDateTemplate = (props: any): React.ReactElement => {
  const formattedDay = moment(props.date).format('ddd')
  const formattedDate = moment(props.date).format('DD')

  return (
    <div className="month-header-row">
      <div className="custom-day-header">
        {formattedDay}{' '}
        <span className="custom-date-header">{formattedDate}</span>
      </div>
    </div>
  )
}

type Provider = GroupedAppointmentsByDate[string]['providers'][number]

const ProviderCard: React.FC<{ provider: Provider }> = ({ provider }) => {
  const backgroundColor = getProviderBackgroundColor(
    provider.provider_color?.label,
  )
  const borderColor = getProviderBorderColor(provider.provider_color?.label)
  const textColor = getProviderTextColor(provider.provider_color?.label)

  return (
    <div
      className="card-holder"
      style={{ backgroundColor, borderColor, color: textColor }}
    >
      <div className="provider-info">
        {`Dr. ${provider.first_name} ${provider.last_name}`}
      </div>
      <div>
        <span className="number" style={{ color: textColor }}>
          {provider.appointments.length}
        </span>
      </div>
    </div>
  )
}

const parsePx = (value: string): number => {
  const parsed = parseFloat(value)
  return Number.isFinite(parsed) ? parsed : 0
}

const outerHeight = (el: HTMLElement): number => {
  const styles = getComputedStyle(el)
  return (
    el.getBoundingClientRect().height +
    parsePx(styles.marginTop) +
    parsePx(styles.marginBottom)
  )
}

const useFitCount = (
  containerRef: React.RefObject<HTMLDivElement>,
  measureRef: React.RefObject<HTMLDivElement>,
  total: number,
): number => {
  const [visibleCount, setVisibleCount] = useState(total)

  useLayoutEffect(() => {
    const container = containerRef.current
    const measure = measureRef.current
    if (!container || !measure) return

    const compute = (): void => {
      if (total === 0) {
        setVisibleCount((prev) => (prev === 0 ? prev : 0))
        return
      }

      const available = container.clientHeight
      const nodes = Array.from(measure.children) as HTMLElement[]
      const cardNodes = nodes.slice(0, total)
      const moreNode = nodes[total]
      if (available <= 0 || cardNodes.length === 0) return

      const gap = parsePx(getComputedStyle(container).rowGap)
      const cardHeights = cardNodes.map(outerHeight)
      const moreHeight = moreNode ? outerHeight(moreNode) : 0

      const cardsHeight = (count: number): number => {
        let height = 0
        for (let i = 0; i < count; i += 1) {
          height += cardHeights[i] + (i > 0 ? gap : 0)
        }
        return height
      }

      let next = total
      if (cardsHeight(total) > available) {
        next = 0
        for (let count = 1; count <= total; count += 1) {
          if (cardsHeight(count) + gap + moreHeight <= available) {
            next = count
          } else {
            break
          }
        }
      }

      setVisibleCount((prev) => (prev === next ? prev : next))
    }

    compute()
    const observer = new ResizeObserver(compute)
    observer.observe(container)
    return () => observer.disconnect()
  }, [containerRef, measureRef, total])

  return Math.min(visibleCount, total)
}

const measureListStyle: React.CSSProperties = {
  position: 'absolute',
  left: -99999,
  top: 0,
  width: '100%',
  visibility: 'hidden',
  pointerEvents: 'none',
}

// Cell content component that can use hooks
const CellContent: React.FC<{
  date: Date
  data: GroupedAppointmentsByDate[string] | undefined
}> = ({ date, data }) => {
  const formattedDate = moment(date).format('YYYY-MM-DD')
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [dropDirection, setDropDirection] = useState<'up' | 'down'>('down')
  const moreInfoHolderRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const measureRef = useRef<HTMLDivElement>(null)

  const handleDropdownToggle = (): void => {
    if (!dropdownOpen && moreInfoHolderRef.current) {
      // Find the actual calendar cell (table cell)
      const calendarCell = moreInfoHolderRef.current.closest(
        '.e-work-cells',
      ) as HTMLElement

      if (calendarCell) {
        // Find the table row containing this cell
        const tableRow = calendarCell.closest('tr') as HTMLTableRowElement
        const tableBody = tableRow?.closest('tbody') as HTMLTableSectionElement

        if (tableRow && tableBody) {
          // Get all rows in the calendar
          const allRows = Array.from(tableBody.querySelectorAll('tr'))
          const currentRowIndex = allRows.indexOf(tableRow)
          const totalRows = allRows.length

          // Check if this cell is in the last 2 rows (bottom rows of calendar)
          const isInBottomRows = currentRowIndex >= totalRows - 2

          // Also check available space above the cell within its container
          const cellRect = moreInfoHolderRef.current.getBoundingClientRect()
          const calendarContent = moreInfoHolderRef.current.closest(
            '.e-content-wrap',
          ) as HTMLElement

          if (calendarContent) {
            const containerRect = calendarContent.getBoundingClientRect()
            const cellTopRelativeToContainer = cellRect.top - containerRect.top
            const estimatedDropdownHeight = 200

            // Only drop up if cell is in bottom rows AND there's enough space above
            if (
              isInBottomRows &&
              cellTopRelativeToContainer > estimatedDropdownHeight
            ) {
              setDropDirection('up')
            } else {
              setDropDirection('down')
            }
          } else {
            // Fallback to down if container not found
            setDropDirection('down')
          }
        } else {
          // Fallback to down if row/table structure not found
          setDropDirection('down')
        }
      } else {
        // Fallback to down if calendar cell not found
        setDropDirection('down')
      }
    }
    setDropdownOpen(!dropdownOpen)
  }

  const providers = data?.providers ?? []
  const total = providers.length
  const visibleCount = useFitCount(contentRef, measureRef, total)
  const visibleProviders = providers.slice(0, visibleCount)
  const remainingProviders = providers.slice(visibleCount)
  const remainingCount = remainingProviders.length

  return (
    <div className="month-cell">
      <Row>
        <Col md={2} className="cell-header-date">
          {moment(date).format('D')}
        </Col>
        <Col
          md={10}
          className="cell-count-text d-flex align-items-center justify-content-end"
        >
          <i className="cell-count-icon mgc_group_3_fill"></i>
          <span>{data?.totalAppointments ?? 0}</span>
        </Col>
      </Row>
      <div className="content-wrapper" ref={contentRef}>
        {data && (
          <>
            <div ref={measureRef} style={measureListStyle} aria-hidden="true">
              {providers.map((provider) => (
                <ProviderCard key={provider.provider_id} provider={provider} />
              ))}
              <div className="more-info-holder">
                <div className="more-info">+{total} more</div>
              </div>
            </div>

            {visibleProviders.map((provider) => (
              <ProviderCard key={provider.provider_id} provider={provider} />
            ))}

            {remainingCount > 0 && (
              <div className="more-info-holder" ref={moreInfoHolderRef}>
                <Dropdown
                  show={dropdownOpen}
                  drop={dropDirection}
                  onToggle={handleDropdownToggle}
                >
                  <Dropdown.Toggle
                    as="div"
                    className="more-info-dropdown-toggle"
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="more-info">+{remainingCount} more</div>
                  </Dropdown.Toggle>
                  <Dropdown.Menu
                    className="more-providers-dropdown"
                    renderOnMount
                    popperConfig={{ strategy: 'fixed' }}
                  >
                    {remainingProviders.map((provider) => (
                      <Dropdown.Item
                        key={provider.provider_id}
                        as="div"
                        className="dropdown-provider-item"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <ProviderCard provider={provider} />
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export const CellTemplate = (
  props: any,
  categorizedAppointments: GroupedAppointmentsByDate,
): React.ReactElement => {
  const formattedDate = moment(props.date).format('YYYY-MM-DD')
  const data = categorizedAppointments[formattedDate]

  return <CellContent date={props.date} data={data} />
}

export const groupAppointmentsByDateAndProvider = (
  appointments: IAppointment[],
): GroupedAppointmentsByDate => {
  const grouped: GroupedAppointmentsByDate = {}

  appointments.forEach((appointment) => {
    const formattedDate = moment(appointment.appt_date).format('YYYY-MM-DD')

    // Initialize the date key if it doesn't exist
    if (!grouped[formattedDate]) {
      grouped[formattedDate] = {
        totalAppointments: 0,
        providers: [],
      }
    }

    const provider = appointment.provider

    if (provider) {
      const { provider_id, first_name, last_name, provider_color } = provider

      // Find or create a provider entry for the current date
      let providerGroup = grouped[formattedDate].providers.find(
        (group) => group.provider_id === provider_id,
      )

      if (!providerGroup) {
        providerGroup = {
          provider_id,
          first_name,
          last_name,
          provider_color,
          appointments: [],
        }
        grouped[formattedDate].providers.push(providerGroup)
      }

      // Add the current appointment to the provider's list of appointments
      providerGroup.appointments.push(appointment)

      // Increment the total appointment count for this date
      grouped[formattedDate].totalAppointments++
    }
  })

  return grouped
}
